/**
 * RelatedDocumentsSection — registry-primary related document display.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { getRelatedDocumentModels } from '../../services/linkPresentationService';
import { eventBridgeService, EVENT_TYPES } from '../../services/eventService';
import './RelatedDocumentsSection.css';

function extractDocumentReferences(contentBlocks) {
  if (!Array.isArray(contentBlocks)) {
    return [];
  }

  return contentBlocks
    .filter((block) => block.type === 'document_reference')
    .map((block) => ({
      documentId: block.documentId,
      documentTitle: block.documentTitle || 'Document',
      linkId: block.registryLinkId || null
    }));
}

const LINK_REFRESH_EVENTS = [
  EVENT_TYPES.LINK_CREATED,
  EVENT_TYPES.LINK_UPDATED,
  EVENT_TYPES.LINK_REMOVED
];

const RelatedDocumentsSection = React.memo(({
  contentBlocks,
  tripId,
  userId,
  hostEntityType,
  hostEntityId,
  className = '',
  hideTitle = false,
  onUnlink
}) => {
  const [registryDocuments, setRegistryDocuments] = useState([]);
  const [readSource, setReadSource] = useState('legacy');
  const [registryLoadVersion, setRegistryLoadVersion] = useState(0);

  const canReadRegistry = Boolean(userId && tripId && hostEntityType && hostEntityId);

  const loadRegistryDocuments = useCallback(async () => {
    if (!canReadRegistry) {
      setRegistryDocuments([]);
      setReadSource('legacy');
      return;
    }

    try {
      const result = await getRelatedDocumentModels(
        userId,
        tripId,
        hostEntityType,
        hostEntityId
      );
      setReadSource(result.source);
      setRegistryDocuments(result.documents || []);
    } catch (error) {
      console.error('[RelatedDocumentsSection] Failed to load registry document links:', error);
      setReadSource('legacy');
      setRegistryDocuments([]);
    }
  }, [canReadRegistry, userId, tripId, hostEntityType, hostEntityId]);

  useEffect(() => {
    loadRegistryDocuments();
  }, [loadRegistryDocuments, registryLoadVersion]);

  useEffect(() => {
    if (!canReadRegistry) {
      return undefined;
    }

    const subscriptions = LINK_REFRESH_EVENTS.map((eventType) =>
      eventBridgeService.subscribe(eventType, (payload) => {
        if (payload?.tripId && payload.tripId !== tripId) {
          return;
        }
        setRegistryLoadVersion((version) => version + 1);
      })
    );

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [canReadRegistry, tripId]);

  const legacyDocumentRefs = useMemo(
    () => extractDocumentReferences(contentBlocks),
    [contentBlocks]
  );

  const displayRefs = useMemo(() => {
    const registryItems = registryDocuments.map((doc) => ({
      key: doc.linkId || doc.documentId,
      documentId: doc.documentId,
      documentTitle: doc.documentTitle
    }));

    const legacyItems = legacyDocumentRefs.map((ref, index) => ({
      key: `${ref.documentId}-${index}`,
      documentId: ref.documentId,
      documentTitle: ref.documentTitle
    }));

    if (readSource === 'registry' && registryItems.length > 0) {
      const seen = new Set(registryItems.map((item) => item.documentId));
      const merged = [...registryItems];
      legacyItems.forEach((item) => {
        if (!seen.has(item.documentId)) {
          seen.add(item.documentId);
          merged.push(item);
        }
      });
      return merged;
    }

    return legacyItems.length > 0 ? legacyItems : registryItems;
  }, [readSource, registryDocuments, legacyDocumentRefs]);

  if (!displayRefs.length) {
    return null;
  }

  const handleDeleteClick = (e, ref) => {
    e.preventDefault();
    e.stopPropagation();
    if (onUnlink) {
      onUnlink('document', ref.documentId, ref.documentTitle);
    }
  };

  return (
    <div className={`related-documents-section ${className}`}>
      {!hideTitle && <h3>Related Documents</h3>}
      <div className="document-chips">
        {displayRefs.map((ref) => (
          <div key={ref.key} className="document-chip-wrapper">
            <Link
              to={`/trips/${tripId}/documents/${ref.documentId}`}
              state={{
                fromEntity: true,
                returnPath: window.location.pathname
              }}
              className="document-chip"
              title={`View ${ref.documentTitle}`}
            >
              {ref.documentTitle}
            </Link>
            {onUnlink && (
              <button
                className="document-unlink-btn"
                type="button"
                onClick={(e) => handleDeleteClick(e, ref)}
                title={`Remove link to ${ref.documentTitle}`}
                aria-label={`Remove link to ${ref.documentTitle}`}
              >
                <FaTimes />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

RelatedDocumentsSection.displayName = 'RelatedDocumentsSection';

export default RelatedDocumentsSection;
